import { apiGet, apiPost, apiPut, apiRoutes, buildApiRoute } from "./entityApiShared.js";
import { requireCmtsIdentifier } from "../core/payloads/cmtsTypedPayloads.js";
import { requireCmtsGuid } from "../core/utils/cmtsGuid.js";
import {
  parseVerificationRecord,
  parseVerificationRecordList,
  type VerificationCreateRequest,
  type VerificationDecisionRequest,
  type VerificationDto,
  type VerificationUpdateRequest,
} from "../../types/verification.types.js";
import type { DropdownOption } from "../types/api.types.js";
import { parseEligibleLifecycleUsers } from "./lifecycleEligibleUsers.js";

export type { VerificationCreateRequest, VerificationDecisionRequest, VerificationDto, VerificationRecordDto, VerificationUpdateRequest } from "../../types/verification.types.js";

type BackendVerificationCreateRequest = Readonly<Omit<VerificationCreateRequest, "complaintId">>;
type BackendVerificationUpdateRequest = Readonly<Omit<VerificationUpdateRequest, "rowVersion"> & { expectedRowVersion?: string | undefined }>;
type BackendVerificationDecisionRequest = Readonly<Omit<VerificationDecisionRequest, "rowVersion"> & { expectedRowVersion?: string | undefined }>;


function createRequest(payload: VerificationCreateRequest): BackendVerificationCreateRequest {
  return {
    verificationRuleId: requireCmtsGuid(payload.verificationRuleId, "Verification create verificationRuleId"),
    verifierUserId: requireCmtsGuid(payload.verifierUserId, "Verification create verifierUserId"),
    pendingStatusId: requireCmtsGuid(payload.pendingStatusId, "Verification create pendingStatusId"),
  };
}

function updateRequest(payload: VerificationUpdateRequest): BackendVerificationUpdateRequest {
  const { rowVersion, ...business } = payload;
  return {
    ...business,
    ...(payload.verificationRuleId ? { verificationRuleId: requireCmtsGuid(payload.verificationRuleId, "Verification update verificationRuleId") } : {}),
    ...(payload.verifierUserId ? { verifierUserId: requireCmtsGuid(payload.verifierUserId, "Verification update verifierUserId") } : {}),
    ...(rowVersion ? { expectedRowVersion: rowVersion } : {}),
  };
}

function decisionRequest(payload: VerificationDecisionRequest): BackendVerificationDecisionRequest {
  const { rowVersion, ...business } = payload;
  return {
    ...business,
    verificationStatusId: requireCmtsGuid(payload.verificationStatusId, "Verification decision verificationStatusId"),
    ...(rowVersion ? { expectedRowVersion: rowVersion } : {}),
  };
}

export const verificationApi = {
  queue: async (signal?: AbortSignal): Promise<readonly VerificationDto[]> => parseVerificationRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.verification.queue), { signal })),
  list: async (complaintId: string, signal?: AbortSignal): Promise<readonly VerificationDto[]> => parseVerificationRecordList(await apiGet<unknown>(buildApiRoute(apiRoutes.verification.list, { complaintId: requireCmtsIdentifier(complaintId, "complaintId") }), { signal })),
  get: async (complaintId: string, id: string, signal?: AbortSignal): Promise<VerificationDto> => parseVerificationRecord(await apiGet<unknown>(buildApiRoute(apiRoutes.verification.get, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "verificationId") }), { signal })),
  permittedVerifiers: async (complaintId: string, signal?: AbortSignal): Promise<readonly DropdownOption[]> => {
    return parseEligibleLifecycleUsers(await apiGet<unknown>(
      buildApiRoute(apiRoutes.verification.permittedVerifiers, {
        complaintId: requireCmtsIdentifier(complaintId, "complaintId"),
      }),
      { signal },
    ));
  },
  create: async (complaintId: string, payload: VerificationCreateRequest, idempotencyKey?: string): Promise<VerificationDto> => parseVerificationRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.verification.create, { complaintId: requireCmtsIdentifier(complaintId, "complaintId") }), createRequest(payload), { idempotencyKey })),
  update: async (complaintId: string, id: string, payload: VerificationUpdateRequest, idempotencyKey?: string): Promise<VerificationDto> => parseVerificationRecord(await apiPut<unknown>(buildApiRoute(apiRoutes.verification.update, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "verificationId") }), updateRequest(payload), { idempotencyKey })),
  pass: async (complaintId: string, id: string, payload: VerificationDecisionRequest, idempotencyKey?: string): Promise<VerificationDto> => parseVerificationRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.verification.pass, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "verificationId") }), decisionRequest(payload), { idempotencyKey })),
  fail: async (complaintId: string, id: string, payload: VerificationDecisionRequest, idempotencyKey?: string): Promise<VerificationDto> => parseVerificationRecord(await apiPost<unknown>(buildApiRoute(apiRoutes.verification.fail, { complaintId: requireCmtsIdentifier(complaintId, "complaintId"), id: requireCmtsIdentifier(id, "verificationId") }), decisionRequest(payload), { idempotencyKey })),
};
